import { addedAt, addedToCampaignAt, IFilter } from "../../List/constants/general-filter";
import { FilterCategory } from "../../List/enums/filter.enum";

// ── Prospect-level filters (Contacts tab) ─────────────────────────────

const name: IFilter = {
	name: "name",
	filterParam: "prospect.name",
	filterByType: ["textContains", "textDoesNotContain"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

const email: IFilter = {
	name: "email",
	filterParam: "prospect.email",
	filterByType: ["isEmpty", "isNotEmpty", "textContains", "textDoesNotContain"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

const titleFilter: IFilter = {
	name: "title",
	filterParam: "prospect.title",
	filterByType: ["dropdownCustom", "dropdownCustomExclude"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

const seniority: IFilter = {
	name: "seniority",
	filterParam: "job_title_to_seniority.seniority",
	filterByType: ["dropdown", "dropdownExclude"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

const linkedinUrl: IFilter = {
	name: "linkedinUrl",
	filterParam: "prospect.linkedinUrl",
	filterByType: [
		"isEmpty",
		"isNotEmpty",
		"textContains",
		"textDoesNotContain",
		"textStartsWith",
		"textEndsWith",
		"textIsExactly",
	],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

export const country: IFilter = {
	name: "country",
	filterParam: "prospect.country",
	filterByType: ["dropdown", "dropdownExclude"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

const city: IFilter = {
	name: "city",
	filterParam: "prospect.city",
	filterByType: ["dropdownCustom", "dropdownCustomExclude"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

// ── Organization-level filters (shared across tabs) ────────────────────

const companyName: IFilter = {
	name: "companyName",
	filterParam: "organization.name",
	filterByType: [
		"textContains",
		"textDoesNotContain",
		"textStartsWith",
		"textEndsWith",
		"textIsExactly",
	],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

export const companyCountry: IFilter = {
	name: "companyCountry",
	filterParam: "organization.country",
	filterByType: ["dropdown", "dropdownExclude"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

const companyCity: IFilter = {
	name: "companyCity",
	filterParam: "organization.city",
	filterByType: ["dropdownCustom", "dropdownCustomExclude"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

// ── Subsidiary / branch-level filters ───────────────────────────────

const countryBranch: IFilter = {
	name: "countryBranch",
	filterParam: "company_location.country",
	filterByType: ["dropdown", "dropdownExclude", "isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const stateBranch: IFilter = {
	name: "stateBranch",
	filterParam: "company_location.state",
	filterByType: ["dropdown", "dropdownExclude", "isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const cityBranch: IFilter = {
	name: "cityBranch",
	filterParam: "organization.city",
	filterByType: ["dropdownCustom", "dropdownCustomExclude", "isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const industry: IFilter = {
	name: "industry",
	filterParam: "organization.industry",
	filterByType: ["dropdown", "dropdownExclude", "isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const estimatedNumEmployees: IFilter = {
	name: "estimatedNumEmployees",
	filterParam: "organization.estimatedNumEmployees",
	filterByType: ["isEmpty", "isNotEmpty", "greaterEqual", "lessEqual", "between"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const companyLinkedinUrl: IFilter = {
	name: "companyLinkedinUrl",
	filterParam: "organization.linkedinUrl",
	filterByType: ["textContains", "textDoesNotContain"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const website: IFilter = {
	name: "website",
	filterParam: "organization.websiteUrl",
	filterByType: [
		"isEmpty",
		"isNotEmpty",
		"textContains",
		"textDoesNotContain",
		"textStartsWith",
		"textEndsWith",
		"textIsExactly",
	],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const foundedYear: IFilter = {
	name: "foundedYear",
	filterParam: "organization.foundedYear",
	filterByType: ["isEmpty", "isNotEmpty", "greaterEqual", "lessEqual", "between"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

// ── Scoring & categorization filters ─────────────────────────────────

export const overallScore: IFilter = {
	name: "overallScore",
	filterParam: "score_persona_category_result.overallScore",
	filterByType: ["isEmpty", "isNotEmpty", "dropdown", "dropdownExclude"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
};

export const companyCategory: IFilter = {
	name: "companyCategory",
	filterParam: "score_company_category_result.scoreCompanyCategoryId",
	subSelectFilterParam: "score_persona_category_result.scoreCompanyCategoryId",
	filterByType: ["isEmpty", "isNotEmpty", "dropdown", "dropdownExclude"],
	filterByValue: [],
	group: FilterCategory.LEAD_CATEGORIZATION_AND_SCORING,
};

export const personaCategory: IFilter = {
	name: "personaCategory",
	filterParam: "score_persona_category_result.scorePersonaCategoryId",
	subSelectFilterParam: "score_persona_category_result.scorePersonaCategoryId",
	filterByType: ["isEmpty", "isNotEmpty", "dropdown", "dropdownExclude"],
	filterByValue: [],
	group: FilterCategory.LEAD_CATEGORIZATION_AND_SCORING,
};

// ── CRM filters ──────────────────────────────────────────────────────

const crmProspect: IFilter = {
	name: "crmProspect",
	filterParam: "crm_prospect.id",
	filterByType: ["isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const crmOrganization: IFilter = {
	name: "crmOrganization",
	filterParam: "crm_organization.id",
	filterByType: ["isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

// ── Deep Research filters ────────────────────────────────────────────

const deepResearchResponse: IFilter = {
	name: "deepResearchResponse",
	filterParam: "deep_research_response.id",
	filterByType: ["isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

// ── List membership filters ─────────────────────────────────────────

export const listFilter: IFilter = {
	name: "list",
	filterParam: "list_person.listId",
	filterByType: ["dropdown", "dropdownExclude"],
	filterByValue: [],
	group: FilterCategory.FREQUENTLY_USED,
	translationKey: "contacts.list",
};

// ── Messaging & status filters (Contacts tab only) ─────────────────────

export const lastContactAt: IFilter = {
	name: "lastContactAt",
	filterParam: "prospect.lastContactAt",
	filterByType: ["isEmpty", "isNotEmpty", "greaterEqualDate", "lessEqualDate", "betweenDate"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const lastResponseAt: IFilter = {
	name: "lastResponseAt",
	filterParam: "prospect.lastResponseAt",
	filterByType: ["isEmpty", "isNotEmpty", "greaterEqualDate", "lessEqualDate", "betweenDate"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const assignStatusValues = [
	{ label: "Draft", value: "DRAFT" },
	{ label: "Saved For Later", value: "SAVED_FOR_LATER" },
	{ label: "Assigning", value: "ASSIGNING" },
	{ label: "Assigned", value: "ASSIGNED" },
	{ label: "Running", value: "RUNNING" },
	{ label: "Blacklist", value: "BLACKLIST" },
	{ label: "Not Now", value: "NOT_NOW" },
	{ label: "No Reply", value: "NO_REPLY" },
	{ label: "Replies", value: "REPLIES" },
	{ label: "To Be Nurtured", value: "TO_BE_NURTURED" },
	{ label: "Do Not Contact", value: "DO_NOT_CONTACT" },
	{ label: "Ended", value: "ENDED" },
	{ label: "Paused", value: "PAUSED" },
	{ label: "Message Sent", value: "MESSAGE_SENT" },
	{ label: "Archived", value: "ARCHIVED" },
	{ label: "Follow Up", value: "FOLLOW_UP" },
	{ label: "Done", value: "DONE" },
	{ label: "Under Review", value: "UNDER_REVIEW" },
	{ label: "Tracked Profile", value: "TRACKED_PROFILE" },
];

export const sentimentValues = [
	{ label: "Interested", value: "INTERESTED" },
	{ label: "Bad Timing", value: "BAD_TIMING" },
	{ label: "Wrong Person", value: "WRONG_PERSON" },
	{ label: "Not Interested", value: "NOT_INTERESTED" },
	{ label: "Neutral", value: "NEUTRAL" },
];

const lastAssignStatus: IFilter = {
	name: "lastAssignStatus",
	filterParam: "list_person.assignStatus",
	filterByType: ["isEmpty", "isNotEmpty", "dropdown", "dropdownExclude"],
	filterByValue: assignStatusValues,
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const lastSentiment: IFilter = {
	name: "lastSentiment",
	filterParam: "conversation.lastSentiment",
	filterByType: ["isEmpty", "isNotEmpty", "dropdown", "dropdownExclude"],
	filterByValue: sentimentValues,
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const firstSentiment: IFilter = {
	name: "firstSentiment",
	filterParam: "conversation.firstSentiment",
	filterByType: ["isEmpty", "isNotEmpty", "dropdown", "dropdownExclude"],
	filterByValue: sentimentValues,
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

/**
 * `bestSentiment` additionally offers the system-derived out-of-office sentiment, because the
 * Analytics "Out-of-office Replies" row drills through on it (PREC-3909). Without the option the
 * incoming filter has no label to render and is lost as soon as the modal is reopened. The
 * user-assignable filters (`lastSentiment` / `firstSentiment`) keep the manual set — OUT_OF_OFFICE
 * is never assigned by hand (see MANUAL_PROSPECT_SENTIMENTS).
 */
export const bestSentimentValues = [
	...sentimentValues,
	{ label: "Out of Office", value: "OUT_OF_OFFICE" },
];

export const bestSentiment: IFilter = {
	name: "bestSentiment",
	filterParam: "conversation.bestSentiment",
	filterByType: ["isEmpty", "isNotEmpty", "dropdown", "dropdownExclude"],
	filterByValue: bestSentimentValues,
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

/**
 * "Replied at all" — the population the Analytics "Replied Leads" tile counts, which is any
 * inbound reply regardless of sentiment (PREC-3909). A presence filter, so it offers only the
 * emptiness operators: `isNotEmpty` = replied, `isEmpty` = never replied.
 */
const hasAnyReply: IFilter = {
	name: "hasAnyReply",
	filterParam: "conversation.hasAnyReply",
	filterByType: ["isEmpty", "isNotEmpty"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

const genderProbability: IFilter = {
	name: "genderProbability",
	filterParam: "prospect.genderProbability",
	filterByType: ["isEmpty", "isNotEmpty", "greaterEqual", "lessEqual", "between"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

// ── Exported filter arrays per tab ─────────────────────────────────────

/** Contacts tab: all prospects */
export const ContactsProspectFilters: IFilter[] = [
	overallScore,
	name,
	titleFilter,
	seniority,
	email,
	country,
	listFilter,
	companyName,
	companyCountry,
	industry,
	estimatedNumEmployees,
	linkedinUrl,
	city,
	companyCity,
	countryBranch,
	stateBranch,
	cityBranch,
	companyLinkedinUrl,
	website,
	foundedYear,
	companyCategory,
	personaCategory,
	crmProspect,
	crmOrganization,
	deepResearchResponse,
	lastContactAt,
	lastResponseAt,
	addedAt,
	addedToCampaignAt,
	lastAssignStatus,
	hasAnyReply,
	lastSentiment,
	firstSentiment,
	bestSentiment,
	genderProbability,
];

const primaryDomain: IFilter = {
	name: "primaryDomain",
	filterParam: "organization.primaryDomain",
	filterByType: ["textContains", "textDoesNotContain"],
	filterByValue: [],
	group: FilterCategory.ADDITIONAL_ATTRIBUTES,
};

/** Companies tab: organizations only */
export const ContactsCompanyFilters: IFilter[] = [
	companyName,
	companyCountry,
	industry,
	estimatedNumEmployees,
	companyCity,
	countryBranch,
	stateBranch,
	cityBranch,
	companyLinkedinUrl,
	website,
	foundedYear,
	primaryDomain,
	crmOrganization,
	companyCategory,
];

/** Competitors tab: organizations + associated prospects */
export const ContactsCompetitorFilters: IFilter[] = [
	companyName,
	companyCountry,
	industry,
	estimatedNumEmployees,
	companyCity,
	companyLinkedinUrl,
	website,
	foundedYear,
];

// ── Blacklist-specific filters ────────────────────────────────────────

const blacklistSource: IFilter = {
	name: "blacklistSource",
	filterParam: "blacklistSource",
	filterByType: ["dropdown", "dropdownExclude"],
	filterByValue: [
		{ label: "Person", value: "PERSON" },
		{ label: "Organization", value: "ORGANIZATION" },
		{ label: "List exclusion", value: "LIST" },
	],
	group: FilterCategory.FREQUENTLY_USED,
};

const blacklistOrigin: IFilter = {
	name: "blacklistOrigin",
	filterParam: "blacklistOrigin",
	filterByType: ["dropdown", "dropdownExclude"],
	filterByValue: [
		{ label: "Unsubscribed by lead", value: "UNSUBSCRIBED_BY_LEAD" },
		{ label: "Blacklisted by user", value: "BLACKLISTED_BY_USER" },
		{ label: "Imported by user", value: "IMPORTED_BY_USER" },
		{ label: "Competitor", value: "COMPETITOR" },
	],
	group: FilterCategory.FREQUENTLY_USED,
};

const snoozed: IFilter = {
	name: "snoozed",
	filterParam: "snoozed",
	filterByType: ["dropdown"],
	filterByValue: [
		{ label: "Yes", value: "true" },
		{ label: "No", value: "false" },
	],
	group: FilterCategory.FREQUENTLY_USED,
};

/** Blacklist tab: same prospect+org filters as Contacts, plus blacklist-specific */
export const ContactsBlacklistFilters: IFilter[] = [
	blacklistSource,
	blacklistOrigin,
	snoozed,
	overallScore,
	name,
	titleFilter,
	seniority,
	email,
	country,
	listFilter,
	companyName,
	companyCountry,
	industry,
	estimatedNumEmployees,
	linkedinUrl,
	city,
	companyCity,
	companyLinkedinUrl,
	website,
	foundedYear,
	companyCategory,
	personaCategory,
];
